import { ChangeEvent, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  ComboBoxV3,
  FormLabel,
  FormItem,
  SelectV2,
  Datepicker,
} from 'components'

import { fetchTechnicians } from 'services/serviceOrder/hooks/useGetTechnicians'
import { formatTime } from 'domains/serviceOrders/utilities/mask/date'
import { maskedInputDate } from 'utilities/date'
import { getHours } from '../../utils'
import {
  convertDateToString,
  convertStringToDate,
  isValidDate,
} from 'utilities/datepicker'

import {
  dayShift as dayShiftValues,
  ScheduleTypeLabel,
  DayShiftLabel,
  ScheduleType,
} from '../../types'
import styles from './styles.module.scss'

const Form = () => {
  const { register, setValue, watch, trigger } = useFormContext()
  const {
    scheduleDateInput,
    serviceOrderType,
    scheduleType,
    dayShift,
    account,
    time,
  } = watch()

  const handleChangeScheduleType = useCallback(
    (value: ScheduleType) => {
      const scheduleActions: Record<ScheduleType, () => void> = {
        DAY_SHIFT: () => {
          setValue('scheduleType', 'DAY_SHIFT')
          setValue('schedulePeriod', 'MORNING')
          setValue('dayShift', 'MORNING')
        },
        BUSINESS_HOURS: () => {
          setValue('scheduleType', 'BUSINESS_HOURS')
          setValue('schedulePeriod', 'BUSINESS_HOURS')
        },
        FIXED_SCHEDULE: () => {
          setValue('scheduleType', 'FIXED_SCHEDULE')
          setValue('schedulePeriod', undefined)
          setValue('dayShift', '')
          setValue('time', '08:00')
        },
      }

      if (value in dayShiftValues) {
        setValue('scheduleType', 'DAY_SHIFT')
        setValue('schedulePeriod', value)
        setValue('dayShift', value)
      } else {
        scheduleActions[value]?.()
      }

      trigger()
    },
    [setValue, trigger],
  )

  const handleChangeScheduleDate = useCallback(
    (value: Date) => {
      setValue('scheduleDate', value.getTime())
      setValue('scheduleDateInput', convertDateToString(value))
    },
    [setValue],
  )

  const handleChangeScheduleDateInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      const parsedDate =
        isValidDate(value) && value.length > 9
          ? convertStringToDate(value)
          : undefined
      setValue('scheduleDate', parsedDate?.getTime() || undefined)
      setValue('scheduleDateInput', maskedInputDate(value))
    },
    [setValue],
  )

  const handleGetTechnicians = useCallback(
    async (searchFilter: string, offset = 0) =>
      await fetchTechnicians(
        account?.id || '',
        serviceOrderType,
        offset,
        searchFilter,
      ),
    [serviceOrderType, account],
  )

  const renderSelect = (
    data: Array<{ name: string; value: string }>,
    value: string,
  ) => (
    <SelectV2.Root valueKey="name" data={data} {...register('scheduleType')}>
      {({ unSelectedOptions }) => (
        <>
          <SelectV2.Field
            value={data.find((item) => item.value === value)?.name}
          />
          <SelectV2.Group>
            {unSelectedOptions?.map((option) => (
              <SelectV2.Option
                key={option.name}
                shouldCloseGroup={false}
                onClick={() => handleChangeScheduleType?.(option.value)}
              >
                {option.name}
              </SelectV2.Option>
            ))}
          </SelectV2.Group>
        </>
      )}
    </SelectV2.Root>
  )

  const timeOptions = getHours()

  return (
    <section className={styles.form}>
      <FormItem>
        <FormLabel>Técnico responsável</FormLabel>
        <ComboBoxV3.Root
          valueKey="name"
          {...register('technician')}
          findOptions={handleGetTechnicians}
        >
          {({ unSelectedOptions }) => (
            <>
              <ComboBoxV3.Field value={watch('technician.name')} />
              <ComboBoxV3.Group>
                {unSelectedOptions?.map((option) => (
                  <ComboBoxV3.Option
                    key={option.id}
                    hasLabel={false}
                    onClick={() => {
                      setValue(
                        'technician',
                        {
                          id: option.id,
                          name: option.name,
                        },
                        {
                          shouldValidate: true,
                        },
                      )
                    }}
                  >
                    {option.name}
                  </ComboBoxV3.Option>
                ))}
              </ComboBoxV3.Group>
            </>
          )}
        </ComboBoxV3.Root>
      </FormItem>

      <div className={styles.right}>
        <FormItem>
          <FormLabel>Data</FormLabel>
          <Datepicker
            selectEnd
            id="scheduleDateModal"
            initialDate={new Date()}
            {...register('scheduleDate')}
            initialValueInput={scheduleDateInput}
            onChangeDate={handleChangeScheduleDate}
            onChangeInput={handleChangeScheduleDateInput}
          />
        </FormItem>

        <FormItem>
          <FormLabel>Agendar em</FormLabel>

          {renderSelect(ScheduleTypeLabel, scheduleType, 'scheduleType')}
        </FormItem>

        {scheduleType === 'FIXED_SCHEDULE' && (
          <FormItem>
            <SelectV2.Root
              valueKey="name"
              data={timeOptions}
              {...register('time')}
            >
              {({ unSelectedOptions }) => (
                <>
                  <SelectV2.Field value={time} />
                  <SelectV2.Group>
                    {unSelectedOptions?.map((option) => (
                      <SelectV2.Option
                        key={option.name}
                        onClick={() =>
                          setValue('time', formatTime(option.name))
                        }
                      >
                        {option.name}
                      </SelectV2.Option>
                    ))}
                  </SelectV2.Group>
                </>
              )}
            </SelectV2.Root>
          </FormItem>
        )}

        {scheduleType === 'DAY_SHIFT' && (
          <FormItem>
            {renderSelect(DayShiftLabel, dayShift, 'dayShift')}
          </FormItem>
        )}
      </div>
    </section>
  )
}

export default Form
